import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import { IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button } from "@material-ui/core";
import { MoreVert, Replay, DeleteOutline } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import AssignAppointmentModal from "../AssignAppointmentModal";
import ConfirmationModal from "../ConfirmationModal";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		display: 'flex',
		alignItems: 'center',
		marginRight: 6,
		flexShrink: 0,
		marginLeft: "auto",
		"& > *": {
			margin: theme.spacing(1),
		},
	},
	messageBox: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		fontStyle: "italic",
		marginBottom: theme.spacing(2),
		borderLeft: `4px solid ${theme.palette.primary.main}`,
  	color: theme.palette.text.primary
	},
}));

const TicketActionButtons = ({ ticket, onTicketUpdate }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
  const { dispatch } = useContext(TicketsContext);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
	const [confirmationFarewellOpen, setConfirmationFarewellOpen] = useState(false);
  const [farewellMessageBody, setFarewellMessageBody] = useState("");
	
  const hasOwnership = user.profile === 'admin' || user.profile === 'superadmin' || user.id === ticket.userId;

	const handleResolveClick = async (e) => {
    const farewellMessage = ticket?.whatsapp?.farewellMessage;
    if (farewellMessage) {
        setFarewellMessageBody(farewellMessage);
        setConfirmationFarewellOpen(true);
    } else {
        handleUpdateTicketStatus(e, "closed", user?.id, false); 
    }
  };
		
	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};

	const handleUpdateTicketStatus = async (e, status, userId, sendFarewellMessage = false) => {
    setLoading(true);
    try {
      const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
        status: status,
        userId: userId || null,
        sendFarewellMessage: sendFarewellMessage,
      });
      
      if (onTicketUpdate) {
        onTicketUpdate(updatedTicket);
      }
      setLoading(false);

      dispatch({
        type: "UPDATE_TICKET",
        payload: updatedTicket,
        meta: { listStatus: status, user: user, selectedTicketId: ticket.id },
      });

      if (updatedTicket.messageError) {
        toast.warn(updatedTicket.messageError, { autoClose: 10000 });
      }

      if (status === "open") {
        history.push(`/tickets/${ticket.id}`);
      } else {
        history.push("/tickets");
      }
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };
  
  const handleOpenAssignAppointmentModal = () => {
    setAssignModalOpen(true);
  };
  
  const handleDeleteTicket = async () => {
		setLoading(true);
		try {
			await api.delete(`/tickets/${ticket.id}`);
			history.push("/tickets"); 
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};
  
	return (
		<div className={classes.actionButtons}>
			{ticket.status === "closed" && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
			{ticket.status === "open" && (
        <>
          <ButtonWithSpinner
            loading={loading}
            startIcon={<Replay />}
            size="small"
            onClick={e => handleUpdateTicketStatus(e, "pending", null)}
          >
            {i18n.t("messagesList.header.buttons.return")}
          </ButtonWithSpinner>

          <ButtonWithSpinner
            loading={loading}
            size="small"
            variant="contained"
            color="primary"
            onClick={handleResolveClick}
        >
            Cerrar
        </ButtonWithSpinner>

          <ButtonWithSpinner
            loading={loading}
            size="small"
            variant="outlined"
            color="secondary"
            onClick={handleOpenAssignAppointmentModal}
          >
            {i18n.t("messagesList.header.buttons.assignAppointment")}
          </ButtonWithSpinner>

          <AssignAppointmentModal
            open={assignModalOpen}
            onClose={() => setAssignModalOpen(false)}
            ticket={ticket}
          /> 
        </>
      )}

			{ticket.status === "pending" && (
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
			)}
      
      {ticket.status === "rejected" && (
				<>
					<ButtonWithSpinner
						loading={loading}
						startIcon={<DeleteOutline />}
						size="small"
						color="secondary"
						variant="contained"
						onClick={() => setConfirmationOpen(true)}
					>
						{i18n.t("ticketOptionsMenu.delete")}
					</ButtonWithSpinner>
				</>
			)}
			
      {hasOwnership && (
        <>
          <Tooltip
            arrow
            placement="bottom"
            title={
              <div style={{ fontSize: '14px', textAlign: 'center' }}>
                <p style={{ margin: 0 }}>{ticket.title ? ticket.title : ""}</p>
                <p style={{ margin: 0 }}>{ticket?.contact?.name} #{ticket.id}</p>
                {ticket.user && (
                  <p style={{ margin: 0 }}>Asignado a: {ticket.user.name}</p>
                )}
              </div>
            }
          >
            <IconButton onClick={handleOpenTicketOptionsMenu}>
              <MoreVert />
            </IconButton>
          </Tooltip>
          <TicketOptionsMenu
            ticket={ticket}
            anchorEl={anchorEl}
            menuOpen={ticketOptionsMenuOpen}
            handleClose={handleCloseTicketOptionsMenu}
          />
        </>
      )}
					
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${ticket.id}?`}
				open={confirmationOpen}
				onClose={() => setConfirmationOpen(false)}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			<Dialog open={confirmationFarewellOpen} onClose={() => setConfirmationFarewellOpen(false)}>
        <DialogTitle>Cerrar Ticket</DialogTitle>
        <DialogContent dividers>
          <DialogContentText color="textPrimary">
						Se ha detectado un mensaje de despedida automático configurado para esta conexión:
					</DialogContentText>
					<div className={classes.messageBox}>
						"{farewellMessageBody}"
					</div>
					<DialogContentText>
						¿Desea enviar este mensaje al cliente antes de cerrar el ticket?
					</DialogContentText>
        </DialogContent>
        <DialogActions>
        	<Button 
        	  onClick={() => {
        	    setConfirmationOpen(false);
        	    handleUpdateTicketStatus(null, "closed", user?.id, false);
        	  }} 
        	  color="primary"
        	>
        	  No enviar
        	</Button>
        	<Button 
        	  onClick={() => {
        	    setConfirmationOpen(false);
        	    handleUpdateTicketStatus(null, "closed", user?.id, true);
        	  }} 
        	  color="primary" 
        	  variant="contained" 
        	  autoFocus
        	>
        	  Enviar y Cerrar
        	</Button>
        </DialogActions>
      </Dialog>
		</div>
	);
};

export default TicketActionButtons;
